import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  TextField,
} from '@mui/material';
import { ArrowBack as BackIcon, Save as SaveIcon, Clear as ClearIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { getStructure, TutorStructureNode } from '../services/tutorStructureService';
import { deleteSchedule, listSchedules, TopicScheduleEntry, upsertSchedule } from '../services/tutorScheduleService';

const flattenTopicNodes = (nodes: TutorStructureNode[]): TutorStructureNode[] => {
  const result: TutorStructureNode[] = [];
  const visit = (node: TutorStructureNode) => {
    if (node.nodeType === 'TOPIC_REF') result.push(node);
    node.children?.forEach(visit);
  };
  nodes.forEach(visit);
  return result;
};

const TutorTopicSchedulePage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const id = Number(courseId);

  const [topics, setTopics] = useState<TutorStructureNode[]>([]);
  const [scheduleMap, setScheduleMap] = useState<Record<number, string>>({});
  const [draftMap, setDraftMap] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<number | null>(null);
  const [clearing, setClearing] = useState<number | null>(null);

  useEffect(() => {
    Promise.all([getStructure(id), listSchedules(id)])
      .then(([structure, schedules]) => {
        setTopics(flattenTopicNodes(structure.nodes));
        const map: Record<number, string> = {};
        schedules.forEach((s: TopicScheduleEntry) => { map[s.nodeId] = s.targetDate; });
        setScheduleMap(map);
        setDraftMap({ ...map });
      })
      .catch((err) => setError(err?.response?.data?.message || err.message || 'Failed to load data'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleSave = async (nodeId: number) => {
    const date = draftMap[nodeId];
    if (!date) return;
    setSaving(nodeId);
    try {
      await upsertSchedule(id, nodeId, date);
      setScheduleMap((prev) => ({ ...prev, [nodeId]: date }));
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to save date');
    } finally {
      setSaving(null);
    }
  };

  const handleClear = async (nodeId: number) => {
    setClearing(nodeId);
    try {
      await deleteSchedule(id, nodeId);
      setScheduleMap((prev) => { const next = { ...prev }; delete next[nodeId]; return next; });
      setDraftMap((prev) => { const next = { ...prev }; delete next[nodeId]; return next; });
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to clear date');
    } finally {
      setClearing(null);
    }
  };

  const isDirty = (nodeId: number) => draftMap[nodeId] !== scheduleMap[nodeId];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Button size="small" startIcon={<BackIcon />} onClick={() => navigate(`/tutor/courses/${id}`)}>
          Course Dashboard
        </Button>
      </Box>

      <Typography variant="h5" gutterBottom>Topic Scheduling</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Set target dates to guide students through the course topics.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {topics.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No topics found for this course. Add topics in the Topic Structure first.</Typography>
          <Button sx={{ mt: 2 }} onClick={() => navigate(`/tutor/courses/${id}/structure`)}>
            Go to Topic Structure
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Topic</TableCell>
                <TableCell width={200}>Target date</TableCell>
                <TableCell width={160} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topics.map((topic) => (
                <TableRow key={topic.id}>
                  <TableCell>{topic.label}</TableCell>
                  <TableCell>
                    <TextField
                      type="date"
                      size="small"
                      value={draftMap[topic.id] || ''}
                      onChange={(e) => setDraftMap((prev) => ({ ...prev, [topic.id]: e.target.value }))}
                      inputProps={{ min: new Date().toISOString().split('T')[0] }}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={saving === topic.id ? <CircularProgress size={14} /> : <SaveIcon />}
                        disabled={saving === topic.id || !draftMap[topic.id] || !isDirty(topic.id)}
                        onClick={() => handleSave(topic.id)}
                      >
                        Save
                      </Button>
                      {scheduleMap[topic.id] && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={clearing === topic.id ? <CircularProgress size={14} /> : <ClearIcon />}
                          disabled={clearing === topic.id}
                          onClick={() => handleClear(topic.id)}
                        >
                          Clear
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default TutorTopicSchedulePage;
